import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const helpers = createMultiStyleConfigHelpers([
  'root',
  'textContainer',
  'media',
  'group1',
  'group2',
  'text1',
  'text2',
  'text3',
  'text3Row',
  'text3Icon',
  'text4'
]);

/**
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {import('@chakra-ui/styled-system').PartsStyleInterpolation<{
 *  keys: ("root" | "textContainer" | "media" | "group1" | "group2" | "text1" | "text2" | "text3" | "text3Row" | "text3Icon" | "text4")[]
 * }>} CarouselItemStyles
 */
export const carouselItemStyles = helpers.defineMultiStyleConfig({
  /**
   * @param {Object} args - Component arguments
   * @param {Contentful.CarouselItem} args.component - CarouselItem component data
   * @param {boolean} [args.isComplete] - Whether component has all fields
   * @param {boolean} [args.isFetching] - Whether additional data is being fetched
   *
   * @returns {CarouselItemStyles} - Component styles
   */
  baseStyle: ({component}) => {
  },
  variants: {
    /**
     * @param {Object} args - Component arguments
     * @param {Contentful.MediaCarousel} args.component - MediaCarousel component data
     * @param {boolean} [args.isComplete] - Whether component has all fields
     * @param {boolean} [args.isFetching] - Whether additional data is being fetched
     *
     * @returns {MediaCarouselStyles} - Component styles
     */
    'CF-011': (args) => ({
      text1: {
        color: 'brandColors.primaryBG',
        fontSize: {base: '28px', lg: '40px'},
        fontWeight: 500,
        left: '50%',
        letterSpacing: {base: '-0.28px', lg: '-0.4px'},
        lineHeight: 'shorter',
        position: 'absolute',
        textAlign: 'center',
        top: {base: '45px', lg: '40%'},
        transform: {base: 'translate(-50%, 0)', lg: 'translate(-50%, -50%)'},
        width: {base: 'calc(100% - 40px)', lg: '80%'},
        zIndex: 1,
      },
    }),

    'CF-012.01': (args) => ({
      group1: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        textAlign: 'center',
      },
      group2: {
        display: 'flex',
        flexDirection: {base: 'column', lg: 'row'},
        gap: {base: 4, lg: 10},
      },
      text1: {
        fontSize: {base: '12px', lg: '14px'},
        fontWeight: '500',
        letterSpacing: {base: '1.2px', lg: '1.4px'},
        textTransform: 'uppercase',
      },
      text2: {
        fontSize: {base: '28px', lg: '46px'},
        fontWeight: '500',
        lineHeight: 'shorter',
      },
      text3Row: {
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: {base: '12px', lg: '16px'},
        minW: 0,
      },
      text3Icon: {
        flexShrink: 0,
        height: {base: '28px', lg: '32px'},
        width: 'auto',
        maxW: {base: '56px', lg: '64px'},
        mt: {base: '2px', lg: '3px'},
        objectFit: 'contain',
      },
      text3: {
        flex: 1,
        fontSize: {base: '24px', lg: '28px'},
        fontWeight: '500',
        letterSpacing: {base: '1.2px', lg: '1.4px'},
        lineHeight: 'shorter',
        minW: 0,
      },
      text4: {
        flex: 1,
        fontSize: {base: '12px', lg: '18px'},
        letterSpacing: {lg: '-0.18px'},
      },
      textContainer: {
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        gap: '180px',
        height: {base: 'auto', lg: '100%'},
        left: {base: '20px', lg: '50%'},
        position: 'absolute',
        top: {base: '48px', lg: '120px'},
        bottom: {base: '100px', lg: 'auto'},
        transform: {base: 'none', lg: 'translateX(-50%)'},
        width: {base: 'calc(100% - 43px)', lg: '60%'},
        maxWidth: {base: 'none', lg: '1200px'},
        zIndex: 10,
        justifyContent: {base: 'space-between', lg: 'unset'},
      },
    }),

    /**
     * CF-022 — collections rail: full-bleed 3:4 card, top title stack, bottom category + links (Figma CF-022).
     * Set **Appearance** on each slide to `CF-022`. Recommended: text2 = main headline, text1 = subtitle line;
     * text3 = category; text4 = uppercase link labels (single field, space-separated).
     */
    'CF-022': () => ({
      root: {
        aspectRatio: '3/4',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
      },
      media: {
        height: '100%',
        left: 0,
        position: 'absolute',
        top: 0,
        width: '100%',
        zIndex: 0,
        sx: {
          '& .component--image, & .component--image img, & picture, & picture img, & img': {
            display: 'block',
            height: '100%',
            objectFit: 'cover',
            width: '100%',
          },
        },
      },
      group1: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: {base: 2, md: 3},
        textAlign: 'center',
      },
      group2: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: {base: 3, md: 4},
        maxW: '100%',
        width: '100%',
      },
      text1: {
        color: 'white',
        fontSize: {base: '10px', md: '12px'},
        fontWeight: '500',
        letterSpacing: {base: '2px', md: '2.4px'},
        lineHeight: 'shorter',
        textTransform: 'uppercase',
      },
      text2: {
        color: 'white',
        fontSize: {base: '22px', md: '32px'},
        fontWeight: '500',
        letterSpacing: {base: '-0.22px', md: '-0.32px'},
        lineHeight: 'shorter',
        textTransform: 'uppercase',
      },
      text3Row: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
        gap: {base: 2, md: 3},
        justifyContent: 'center',
        minW: 0,
      },
      text3Icon: {
        flexShrink: 0,
        height: {base: '24px', md: '28px'},
        maxW: {base: '48px', md: '56px'},
        mt: 0,
        objectFit: 'contain',
        width: 'auto',
      },
      text3: {
        color: 'white',
        flex: 'none',
        fontSize: {base: '14px', md: '16px'},
        fontWeight: '500',
        letterSpacing: {base: '1.4px', md: '1.6px'},
        lineHeight: 'shorter',
        textAlign: 'center',
        textTransform: 'uppercase',
      },
      text4: {
        color: 'white',
        fontSize: {base: '9px', md: '11px'},
        fontWeight: '400',
        letterSpacing: {base: '0.12em', md: '0.14em'},
        lineHeight: {base: '150%', md: '140%'},
        maxW: '100%',
        opacity: 0.95,
        textAlign: 'center',
        textTransform: 'uppercase',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
      textContainer: {
        background:
          'linear-gradient(180deg, rgba(27, 26, 24, 0.55) 0%, rgba(27, 26, 24, 0.15) 35%, rgba(27, 26, 24, 0.2) 65%, rgba(27, 26, 24, 0.75) 100%)',
        bottom: 0,
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'space-between',
        left: 0,
        paddingBottom: {base: 6, md: 8},
        paddingTop: {base: 7, md: 10},
        paddingX: {base: 4, md: 5},
        position: 'absolute',
        right: 0,
        textAlign: 'center',
        top: 0,
        width: '100%',
        zIndex: 2,
      },
    }),

    /**
     * @param {Object} args - Component arguments
     * @param {Contentful.MediaCarousel} args.component - MediaCarousel component data
     * @param {boolean} [args.isComplete] - Whether component has all fields
     * @param {boolean} [args.isFetching] - Whether additional data is being fetched
     *
     * @returns {MediaCarouselStyles} - Component styles
     */
    'CF-013': (args) => ({
      root: {
        maxWidth: '100%',
        width: '100%',
      },
      media: {
        display: 'block',
        maxWidth: '100%',
        width: '100%',
        sx: {
          '& .component--image, & .component--image img, & picture img, & img': {
            display: 'block',
            height: 'auto',
            maxWidth: '100%',
            width: '100%',
          },
        },
      },
      group1: {
        margin: {base: '48px auto 8px', lg: '120px auto 46px'},
        textAlign: 'center',
        width: {base: '100%', lg: 'calc(100% - 600px)'},
        paddingX: [4, 4, 6, 6, 8, 8],
      },
      text1: {
        fontSize: {base: '12px', lg: '14px'},
        fontWeight: 500,
        letterSpacing: {base: '1.2px', lg: '1.4px'},
        marginBottom: '12px',
        textTransform: 'uppercase',
      },
      text2: {
        fontSize: {base: '28px', lg: '46px'},
        fontWeight: 500,
        color: 'brandColors.extraDarkCharcoal',
        lineHeight: 'shorter',
        marginBottom: '16px',
      },
      text3: {
        fontSize: {base: '16px', lg: '20px'},
        lineHeight: '150%',
      },
    }),
  },
});
